/**
 * Shared bulk-upload dialog for Purchase + Sales invoices.
 * — First pick the shared config (Purchase: category ledger; Sales: B2B/B2C)
 * — Then drop N files; we fire the existing single-file /extract endpoint
 *   per file with the config pinned as query params
 * — Runs max `CONCURRENCY` uploads at once and shows per-file progress
 * — Every finished draft lands in the normal drafts list (no auto-post)
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { X, UploadSimple, CheckCircle, XCircle, CircleNotch, Warning } from "@phosphor-icons/react";

const CONCURRENCY = 3;

function pill(state) {
    switch (state) {
        case "queued":  return { c: "bg-stone-100 text-stone-500 border-stone-300", l: "queued" };
        case "uploading": return { c: "bg-blue-100 text-blue-800 border-blue-300", l: "uploading" };
        case "extracting": return { c: "bg-amber-100 text-amber-800 border-amber-300", l: "AI reading" };
        case "done": return { c: "bg-emerald-100 text-emerald-800 border-emerald-300", l: "draft ready" };
        case "duplicate": return { c: "bg-orange-100 text-orange-800 border-orange-300", l: "duplicate" };
        case "failed": return { c: "bg-red-100 text-red-800 border-red-300", l: "failed" };
        default: return { c: "bg-stone-100 text-stone-500 border-stone-300", l: state };
    }
}


export default function BulkUploadDialog({
    open, onClose, kind, // "purchase" | "sales"
    // Purchase-only:
    categories = [],
    // Sales-only:
    // (no external props needed)
    onAllDone,
}) {
    const [step, setStep] = useState("config"); // config | upload | done
    const [files, setFiles] = useState([]); // {file, state, message, draftId}
    // Purchase config
    const [categoryId, setCategoryId] = useState("");
    const [isAsset, setIsAsset] = useState(false);
    // Sales config
    const [salesType, setSalesType] = useState("b2c"); // b2b | b2c

    useEffect(() => {
        if (!open) { setStep("config"); setFiles([]); setCategoryId(""); setIsAsset(false); setSalesType("b2c"); }
    }, [open]);

    const selectedCategoryName = useMemo(() =>
        categories.find((c) => c.id === categoryId)?.name || "", [categories, categoryId]);

    const totals = useMemo(() => {
        const done = files.filter((f) => f.state === "done").length;
        const dup  = files.filter((f) => f.state === "duplicate").length;
        const fail = files.filter((f) => f.state === "failed").length;
        return { done, dup, fail, total: files.length };
    }, [files]);

    if (!open) return null;

    const addFiles = (list) => {
        const arr = Array.from(list || []);
        if (!arr.length) return;
        setFiles((prev) => [...prev,
            ...arr.map((f) => ({ file: f, state: "queued", message: "", draftId: null }))]);
    };

    const goToUpload = () => {
        if (kind === "purchase" && !categoryId && !isAsset) {
            return toast.error("Pick the expense/asset category the whole batch belongs to.");
        }
        setStep("upload");
    };

    const startUploads = async () => {
        const queue = files.map((f, i) => ({ ...f, _idx: i })).filter((f) => f.state === "queued");
        // Simple concurrency pool
        let cursor = 0;
        const worker = async () => {
            while (cursor < queue.length) {
                const item = queue[cursor++];
                await uploadOne(item);
            }
        };
        setFiles((prev) => prev.map((f) => f.state === "queued" ? { ...f, state: "uploading" } : f));
        await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker));
        setStep("done");
        onAllDone?.();
    };

    const uploadOne = async (item) => {
        const setState = (patch) => setFiles((prev) =>
            prev.map((f, i) => i === item._idx ? { ...f, ...patch } : f));
        try {
            setState({ state: "uploading", message: "" });
            const fd = new FormData();
            fd.append("file", item.file);
            const params = { doc_type: "invoice" };
            let url = "";
            if (kind === "purchase") {
                url = "/purchase-invoices/extract";
                if (categoryId) params.bulk_category_ledger_id = categoryId;
                if (isAsset) params.bulk_is_asset = true;
            } else {
                url = "/sales-invoices/extract";
                params.bulk_is_b2b = salesType === "b2b";
            }
            const { data } = await api.post(url, fd, {
                headers: { "Content-Type": "multipart/form-data" },
                params,
            });
            const draftId = data.id;
            setState({ state: "extracting", draftId });

            // Poll until extraction finishes (or fails) — up to 90s
            const detailUrl = kind === "purchase"
                ? `/purchase-invoices/${draftId}`
                : `/sales-invoices/${draftId}`;
            const started = Date.now();
            while (Date.now() - started < 90_000) {
                await new Promise((r) => setTimeout(r, 1800));
                try {
                    const { data: doc } = await api.get(detailUrl);
                    if (doc.status === "draft") {
                        if (doc.duplicate_of) {
                            setState({ state: "duplicate",
                                       message: `Possible duplicate of #${doc.duplicate_info?.invoice_no || doc.duplicate_of.slice(0, 6)}` });
                        } else {
                            setState({ state: "done", message: `#${doc.invoice_no || draftId.slice(0, 6)} · ₹${(doc.total || 0).toLocaleString("en-IN")}` });
                        }
                        return;
                    }
                    if (doc.status === "extraction_failed") {
                        setState({ state: "failed", message: doc.extraction_error || "extraction failed" });
                        return;
                    }
                } catch { /* keep polling */ }
            }
            setState({ state: "extracting", message: "AI still processing — draft will finish in background" });
        } catch (e) {
            const msg = formatApiError(e);
            const isDupBlock = /duplicate/i.test(msg);
            setState({ state: isDupBlock ? "duplicate" : "failed", message: msg });
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             data-testid="bulk-upload-dialog">
            <div className="bg-white rounded-md border shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
                 style={{ borderColor: "var(--se-border)" }}>
                <div className="px-5 py-4 border-b flex items-center justify-between"
                     style={{ borderColor: "var(--se-border)" }}>
                    <div>
                        <div className="text-lg font-serif">
                            Bulk upload · {kind === "purchase" ? "Purchase invoices" : "Sales invoices"}
                        </div>
                        <div className="text-[11px] text-stone-500 mt-0.5">
                            {step === "config"
                                ? (kind === "purchase"
                                    ? "Pick ONE expense/asset category so every file in this batch is pinned to it. Wrong-ledger risk = zero."
                                    : "Pick B2B or B2C for the whole batch. Every file will be pinned to that type.")
                                : step === "upload"
                                    ? "Drop files below. Duplicates get flagged (not blocked) — you can review each draft after."
                                    : `${totals.done} ready · ${totals.dup} duplicates · ${totals.fail} failed`}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-stone-400 hover:text-stone-700" data-testid="bulk-close">
                        <X size={18} />
                    </button>
                </div>

                {step === "config" ? (
                    <div className="p-6 space-y-5">
                        {kind === "purchase" ? (
                            <>
                                <div>
                                    <label className="text-[10px] uppercase tracking-widest text-stone-500">Expense / asset category (pinned on every file)</label>
                                    <select value={categoryId}
                                            onChange={(e) => { setCategoryId(e.target.value); setIsAsset(false); }}
                                            className="border rounded-sm px-3 py-2 text-sm w-full mt-1"
                                            data-testid="bulk-category">
                                        <option value="">— pick a category —</option>
                                        {categories.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.name} {c.group_name ? `· ${c.group_name}` : ""}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input type="checkbox" checked={isAsset}
                                            onChange={(e) => { setIsAsset(e.target.checked); if (e.target.checked) setCategoryId(""); }}
                                            data-testid="bulk-is-asset" />
                                    All files in this batch are <b>capex / fixed assets</b> (bypass expense head; asset category picked per-draft)
                                </label>
                            </>
                        ) : (
                            <div>
                                <label className="text-[10px] uppercase tracking-widest text-stone-500">Type of sales (pinned on every file)</label>
                                <div className="flex gap-3 mt-2">
                                    <label className={`flex-1 border rounded-sm px-4 py-3 cursor-pointer text-center ${salesType === "b2b" ? "border-purple-600 bg-purple-50" : ""}`}
                                           style={{ borderColor: salesType === "b2b" ? "#7c3aed" : "var(--se-border)" }}>
                                        <input type="radio" checked={salesType === "b2b"} onChange={() => setSalesType("b2b")}
                                                className="hidden" data-testid="bulk-sales-b2b" />
                                        <div className="font-medium">B2B</div>
                                        <div className="text-[10px] text-stone-500 mt-1">Customer has GSTIN · invoice-level GST</div>
                                    </label>
                                    <label className={`flex-1 border rounded-sm px-4 py-3 cursor-pointer text-center ${salesType === "b2c" ? "border-purple-600 bg-purple-50" : ""}`}
                                           style={{ borderColor: salesType === "b2c" ? "#7c3aed" : "var(--se-border)" }}>
                                        <input type="radio" checked={salesType === "b2c"} onChange={() => setSalesType("b2c")}
                                                className="hidden" data-testid="bulk-sales-b2c" />
                                        <div className="font-medium">B2C</div>
                                        <div className="text-[10px] text-stone-500 mt-1">Retail · no GSTIN required</div>
                                    </label>
                                </div>
                            </div>
                        )}
                        <div className="text-[11px] text-stone-500 bg-stone-50 border rounded-sm p-3"
                             style={{ borderColor: "var(--se-border)" }}>
                            <b>How it works:</b> Every uploaded file is extracted by AI and saved as a
                            <b> draft</b> with your pinned {kind === "purchase" ? "category" : "B2B/B2C setting"}. Nothing
                            posts to the ledger. Duplicates get flagged; a Founder / Accountant reviews and posts.
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={onClose} data-testid="bulk-cancel">Cancel</Button>
                            <Button onClick={goToUpload}
                                    style={{ background: "var(--se-brand)", color: "#fff" }}
                                    data-testid="bulk-continue">Continue →</Button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="p-6 space-y-4 overflow-auto flex-1">
                            {kind === "purchase" ? (
                                <div className="text-[11px] px-3 py-2 rounded-sm bg-amber-50 border border-amber-300 text-amber-900" data-testid="bulk-pin-hint">
                                    Pinned category: <b>{isAsset ? "Fixed Asset (per-draft)" : selectedCategoryName || "—"}</b>
                                </div>
                            ) : (
                                <div className="text-[11px] px-3 py-2 rounded-sm bg-amber-50 border border-amber-300 text-amber-900">
                                    Pinned type: <b>{salesType.toUpperCase()}</b>
                                </div>
                            )}

                            <label className="block border-2 border-dashed rounded-md p-6 text-center cursor-pointer hover:bg-stone-50"
                                    style={{ borderColor: "var(--se-border)" }}
                                    data-testid="bulk-dropzone">
                                <input type="file" multiple
                                        accept="application/pdf,image/*"
                                        onChange={(e) => addFiles(e.target.files)}
                                        className="hidden" />
                                <UploadSimple size={28} className="mx-auto text-stone-400 mb-2" />
                                <div className="text-sm font-medium">Click to add files (or drop here)</div>
                                <div className="text-[10px] text-stone-500 mt-1">PDF · JPG · PNG · WEBP · multiple allowed</div>
                            </label>

                            {files.length ? (
                                <div className="border rounded-md overflow-hidden"
                                     style={{ borderColor: "var(--se-border)" }}
                                     data-testid="bulk-file-list">
                                    {files.map((f, i) => {
                                        const p = pill(f.state);
                                        return (
                                            <div key={i} className="px-4 py-3 border-b flex items-center gap-3 text-sm last:border-b-0"
                                                 style={{ borderColor: "var(--se-border)" }}>
                                                <div className="flex-1 min-w-0">
                                                    <div className="truncate">{f.file.name}</div>
                                                    <div className="text-[10px] text-stone-500 truncate">{f.message || `${Math.round(f.file.size / 1024)} KB`}</div>
                                                </div>
                                                <span className={`text-[10px] px-2 py-0.5 rounded-sm border ${p.c} whitespace-nowrap`}>
                                                    {f.state === "uploading" || f.state === "extracting"
                                                        ? <CircleNotch size={10} className="inline animate-spin mr-1" />
                                                        : f.state === "done"
                                                            ? <CheckCircle size={10} weight="fill" className="inline mr-1" />
                                                            : f.state === "duplicate"
                                                                ? <Warning size={10} weight="fill" className="inline mr-1" />
                                                                : f.state === "failed"
                                                                    ? <XCircle size={10} weight="fill" className="inline mr-1" />
                                                                    : null}
                                                    {p.l}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : null}
                        </div>
                        <div className="px-6 py-3 border-t flex justify-between items-center gap-3 flex-wrap"
                             style={{ borderColor: "var(--se-border)" }}>
                            <div className="text-[11px] text-stone-500">
                                {step === "done"
                                    ? `Done. ${totals.done} draft${totals.done === 1 ? "" : "s"} ready · ${totals.dup} flagged as duplicate · ${totals.fail} failed`
                                    : `${files.length} file${files.length === 1 ? "" : "s"} queued`}
                            </div>
                            <div className="flex gap-2">
                                {step !== "done" ? (
                                    <>
                                        <Button variant="outline" onClick={() => setStep("config")}
                                                data-testid="bulk-back">← Back</Button>
                                        <Button onClick={startUploads} disabled={!files.length}
                                                style={{ background: "var(--se-brand)", color: "#fff" }}
                                                data-testid="bulk-start">
                                            <UploadSimple size={14} className="mr-2" />
                                            Upload {files.length ? `(${files.length})` : ""}
                                        </Button>
                                    </>
                                ) : (
                                    <Button onClick={onClose}
                                            style={{ background: "var(--se-brand)", color: "#fff" }}
                                            data-testid="bulk-finish">Close · go to drafts</Button>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
